import { AxiosResponse } from "axios";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types/response";

import { unpackError } from "@dashboard/devices/api/helpers";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import HttpService from "@services/httpService";
import RouteService from "@services/routeService";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";

import { ResellerClient } from "../types";

export interface CreateClientPayload {
    workspaceId: number;
    organizationName: string;
    licenseLimit: number;
    resellerDescription?: string;
}

export interface FetchClientsParams {
    workspaceId: number | undefined;
    pageIndex?: number;
    pageSize?: number;
    sortCriteria?: string;
    sortDirection?: string;
}
type CreateClientBody = {
    organizationName: string;
    licenseLimit: number;
    resellerDescription?: string;
};

export const createClient = async (payload: CreateClientPayload): Promise<ResellerClient> => {
    const { workspaceId, organizationName, licenseLimit, resellerDescription } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const body: CreateClientBody = { organizationName, licenseLimit };

        if (resellerDescription) {
            body.resellerDescription = resellerDescription;
        }

        const response = await HttpService.post<CreateClientBody, AxiosResponse<ApiResponse<ResellerClient>>>(
            endpoint + `${workspaceId}/clients`,
            body,
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

export type FetchClientsResponse = PaginatedResponse<ResellerClient>;
export const fetchClients = async (params: FetchClientsParams): Promise<FetchClientsResponse> => {
    const { workspaceId, pageIndex = 1, pageSize = 5, sortCriteria, sortDirection } = params;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const queryParams = new URLSearchParams({
            pageIndex: String(pageIndex + pageIndexOffset),
            pageSize: String(pageSize),
        });

        if (sortCriteria) {
            queryParams.append("sortCriteria", sortCriteria);
        }

        if (sortDirection !== undefined) {
            queryParams.append("sortDirection", sortDirection);
        }

        const response = await HttpService.get<FetchClientsResponse>(
            `${endpoint}${workspaceId}/clients?${queryParams.toString()}`,
        );
        return response.data;
    } catch (error) {
        throw unpackError(error);
    }
};

export interface FetchClientParams {
    workspaceId: number;
    clientId: number;
}

export const fetchClient = async (params: FetchClientParams): Promise<ResellerClient> => {
    const { workspaceId, clientId } = params;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const response = await HttpService.get<ApiResponse<ResellerClient>>(`${endpoint}${workspaceId}/clients/${clientId}`);
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

interface MemberUpdatePayload {
    workspaceId: number;
    clientId: number;
    addedMembers?: Record<string, WorkspaceMember["role"]>;
    changedMembers?: Record<string, WorkspaceMember["role"]>;
    removedMembers?: string[];
}
export const updateWorkspaceMembers = async (payload: MemberUpdatePayload) => {
    const { workspaceId, clientId, ...updateData } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const response = await HttpService.patch<typeof updateData, AxiosResponse<ApiResponse<API.Workspace>>>(
            endpoint + `${workspaceId}/clients/${clientId}/members`,
            updateData,
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

interface UpdateClientExpirationPayload {
    workspaceId: number;
    clientId: number;
    date: string;
}

export const updateClientExpiration = async (payload: UpdateClientExpirationPayload): Promise<string> => {
    const { workspaceId, clientId, date } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const response = await HttpService.patch<{ date: string }, AxiosResponse<ApiResponse<string>>>(
            endpoint + `${workspaceId}/clients/${clientId}/expiration`,
            { date },
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

interface CancelClientSubscriptionPayload {
    workspaceId: number;
    clientId: number;
    date: string;
}

export const cancelClientSubscription = async (payload: CancelClientSubscriptionPayload): Promise<string> => {
    const { workspaceId, clientId, date } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const response = await HttpService.patch<{ date: string }, AxiosResponse<ApiResponse<string>>>(
            endpoint + `${workspaceId}/clients/${clientId}/cancellation`,
            { date },
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

interface ReviveClientSubscriptionPayload {
    workspaceId: number;
    clientId: number;
}

export const reviveClientSubscription = async (payload: ReviveClientSubscriptionPayload): Promise<string> => {
    const { workspaceId, clientId } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const response = await HttpService.patch<{ date: null }, AxiosResponse<ApiResponse<string>>>(
            endpoint + `${workspaceId}/clients/${clientId}/cancellation`,
            { date: null },
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

export interface FetchClientFeaturesParams {
    workspaceId: number;
    clientId: number;
    includeUsage?: boolean;
}

export const fetchClientFeatures = async (params: FetchClientFeaturesParams): Promise<ApiResponse<API.Feature[]>> => {
    const { workspaceId, clientId, includeUsage = false } = params;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const queryParams = includeUsage ? { includeUsage: true } : {};
        const response = await HttpService.get<ApiResponse<API.Feature[]>>(
            `${endpoint}${workspaceId}/clients/${clientId}/features`,
            {
                params: queryParams,
            },
        );
        return response.data;
    } catch (error) {
        throw unpackError(error);
    }
};

interface UpdateClientFeaturePayload {
    workspaceId: number;
    clientId: number;
    type: string;
    value: number;
}

export const updateClientFeature = async (payload: UpdateClientFeaturePayload): Promise<ApiResponse<API.Feature>> => {
    const { workspaceId, clientId, type, value } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        const response = await HttpService.patch<{ type: string; value: number }, AxiosResponse<ApiResponse<API.Feature>>>(
            `${endpoint}${workspaceId}/clients/${clientId}/features`,
            { type, value },
        );
        return response.data;
    } catch (error) {
        throw unpackError(error);
    }
};

export interface UpdateClientWorkspacePayload {
    workspaceId: number;
    clientId: number;
    /** Documented as required by the endpoint, so it always rides along even when untouched. */
    name: string;
    organizationName?: string;
    organizationVatId?: string;
    organizationUrl?: string;
    defaultGroupId?: number;
    // The backend patches per field, so an omitted key keeps the current note and only an empty string clears
    // it. Typed as string rather than string | null so no call site can send a null and silently lose a clear.
    resellerDescription?: string;
}

export const updateClientWorkspace = async (payload: UpdateClientWorkspacePayload): Promise<void> => {
    const { workspaceId, clientId, ...updateData } = payload;
    try {
        const endpoint = await RouteService.getWorkspacesRoute();
        // Answers with a message-only envelope, so there is no model to hand back to the caller.
        await HttpService.put<typeof updateData, AxiosResponse<BasicApiResponse>>(
            `${endpoint}${workspaceId}/clients/${clientId}`,
            updateData,
        );
    } catch (error) {
        throw unpackError(error);
    }
};
